/**
 * Memed — prescrição digital (camada de acesso do frontend).
 *
 * Responsabilidades:
 *  - falar com `/clinic/memed/*` (o par api-key/secret-key nunca chega aqui);
 *  - carregar o script `sinapse-prescricao` **uma única vez por sessão**,
 *    com `id` fixo e guard `getElementById` — critério de homologação Memed;
 *  - registrar os listeners de `prescricaoImpressa`/`prescricaoExcluida`
 *    uma única vez, dentro de `core:moduleInit`.
 *
 * @see https://doc.memed.com.br/docs/boas-praticas-integracao/
 */

import axios from '../config/axios';
import { getApiUrl } from '../config/environment';

export const MEMED_MODULE = 'plataforma.prescricao';
/** `id` exigido pela Memed para detectar injeção duplicada do script. */
export const MEMED_SCRIPT_ID = 'memed-prescricao-script';

// ---------------------------------------------------------------------------
// Tipos
// ---------------------------------------------------------------------------

export interface MemedStatus {
  configured: boolean;
  enabled: boolean;
  environment: 'integration' | 'production';
  script_url: string;
  module: string;
  force_sign: boolean;
  signature_providers: string[];
}

export interface MemedSession {
  token: string;
  script_url: string;
  environment: 'integration' | 'production';
  module: string;
  professional: {
    id: string;
    full_name: string;
    memed_user_id: string | null;
    memed_status: string | null;
  };
  workplace: {
    cnes: string;
    local_name: string;
    address: string;
    city: string;
    state: string;
    phone: string;
  };
  feature_toggles: Record<string, unknown>;
  additional_data: { header: Record<string, string>[]; footer: string };
}

export interface MemedPatientPayload {
  idExterno: string;
  nome: string;
  sexo: string;
  [key: string]: unknown;
}

export interface MemedProfessionalStatus {
  professional_id: string;
  full_name: string;
  memed_user_id: string | null;
  memed_status: string | null;
  memed_synced_at: string | null;
  memed_sync_error: string | null;
  ready: boolean;
  missing_fields: string[];
}

export interface MemedPrescriptionDocuments {
  memed_digital_link: string | null;
  memed_unlock_code: string | null;
  memed_pdf_url: string | null;
  /** Cópia do PDF guardada no Klinvia (mesmo storage do ICP-Brasil). */
  signed_pdf_url?: string | null;
  signature_hash?: string | null;
  signature_provider?: string | null;
  signature_verification_code?: string | null;
}

export interface MemedResyncResult {
  prescription: any;
  items_synced: number;
  pdf_stored: boolean;
  documents_error: string | null;
}

/** Item a pré-carregar na Memed ao renovar uma receita (`addItem`). */
export interface MemedPrefillItem {
  memed_item_id?: string | null;
  memed_item_type?: string | null;
  medication_name: string;
  dosage?: string | null;
  instructions?: string | null;
  quantity?: string | null;
  quantity_numeric?: number | null;
}

/** Contexto clínico que amarra a receita Memed ao atendimento no Klinvia. */
export interface MemedPrescriptionContext {
  professional_id?: string | null;
  patient_id?: string | null;
  appointment_id?: string | null;
  medical_record_id?: string | null;
}

declare global {
  interface Window {
    MdSinapsePrescricao?: {
      event: { add: (event: string, handler: (payload: any) => void) => void };
      setToken?: (token: string) => void;
    };
    MdHub?: {
      command: {
        send: (module: string, command: string, params?: any) => Promise<any>;
        /** Disponível no SDK legado; o bridge V4 expõe apenas `send`. */
        ping?: (module: string) => Promise<any>;
      };
      /** No SDK legado `show`/`hide` são síncronos e devolvem `undefined`. */
      module: { show: (module: string) => unknown; hide: (module: string) => unknown };
      event: { add: (event: string, handler: (payload: any) => void) => void };
      server?: { unbindEvents: () => void };
    };
  }
}

// ---------------------------------------------------------------------------
// HTTP
// ---------------------------------------------------------------------------

export async function fetchMemedStatus(): Promise<MemedStatus> {
  const { data } = await axios.get(getApiUrl('/clinic/memed/status'));
  return data.data;
}

export async function createMemedSession(professionalId?: string | null): Promise<MemedSession> {
  const { data } = await axios.post(getApiUrl('/clinic/memed/session'), {
    professional_id: professionalId || undefined,
  });
  return data.data;
}

export async function fetchMemedPatientPayload(patientId: string): Promise<MemedPatientPayload> {
  const { data } = await axios.get(getApiUrl(`/clinic/memed/patients/${patientId}/payload`));
  return data.data;
}

export async function saveMemedPrescription(
  payload: any,
  context: MemedPrescriptionContext,
): Promise<any> {
  const { data } = await axios.post(getApiUrl('/clinic/memed/prescriptions'), {
    payload,
    ...context,
  });
  return data.data;
}

export async function notifyMemedPrescriptionDeleted(memedPrescriptionId: string | number) {
  const { data } = await axios.post(
    getApiUrl(`/clinic/memed/prescriptions/${memedPrescriptionId}/deleted`),
  );
  return data.data;
}

export async function deleteMemedPrescription(prescriptionId: string) {
  const { data } = await axios.delete(getApiUrl(`/clinic/memed/prescriptions/${prescriptionId}`));
  return data.data;
}

export async function refreshMemedDocuments(
  prescriptionId: string,
): Promise<MemedPrescriptionDocuments> {
  const { data } = await axios.post(
    getApiUrl(`/clinic/memed/prescriptions/${prescriptionId}/refresh-documents`),
  );
  return data.data;
}

/** Medicamentos + PDF relidos na Memed e guardados no Klinvia. */
export async function resyncMemedPrescription(prescriptionId: string): Promise<MemedResyncResult> {
  const { data } = await axios.post(
    getApiUrl(`/clinic/memed/prescriptions/${prescriptionId}/resync`),
    {},
    { timeout: 120_000 },
  );
  return data.data;
}

type MemedDocumentSource = {
  id: string;
  memed_pdf_url?: string | null;
  memed_digital_link?: string | null;
  signed_pdf_url?: string | null;
  signature_hash?: string | null;
  signature_provider?: string | null;
};

/** O PDF emitido na Memed já foi copiado para o storage do Klinvia. */
export function hasStoredMemedPdf(prescription: any): boolean {
  return Boolean(
    prescription?.signature_provider === 'memed'
    && (prescription?.signed_pdf_url || prescription?.signature_hash),
  );
}

function openMemedUrl(url: string) {
  window.open(url, '_blank', 'noopener,noreferrer');
}

/** Abre a cópia guardada; a aba é aberta antes do `await` para não ser bloqueada. */
async function openStoredMemedPdf(prescription: MemedDocumentSource, pendingTab?: Window | null): Promise<boolean> {
  const external = String(prescription.signed_pdf_url || '').trim();
  if (/^https?:\/\//i.test(external)) {
    if (pendingTab && !pendingTab.closed) pendingTab.location.replace(external);
    else openMemedUrl(external);
    return true;
  }

  const tab = pendingTab ?? window.open('', '_blank');
  try {
    const response = await axios.get(getApiUrl(`/clinic/prescriptions/${prescription.id}/signed-pdf`), {
      responseType: 'blob',
    });
    const blobUrl = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
    if (tab && !tab.closed) {
      tab.location.replace(blobUrl);
    } else {
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `receituario-memed-${prescription.id.slice(0, 8)}.pdf`;
      link.click();
    }
    window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    return true;
  } catch {
    if (!pendingTab) tab?.close();
    return false;
  }
}

/**
 * Abre o documento da receita Memed. Ordem: cópia guardada no Klinvia (não
 * expira), PDF/link da Memed e, por último, uma nova consulta à Memed.
 */
export async function openMemedPrescriptionDocument(
  prescription: MemedDocumentSource,
  options: { refreshIfMissing?: boolean } = {},
): Promise<{
  opened: boolean;
  documents?: MemedPrescriptionDocuments;
  message?: string;
}> {
  const refreshIfMissing = options.refreshIfMissing !== false;
  const knowsDocuments =
    'memed_pdf_url' in prescription || 'memed_digital_link' in prescription || 'signature_hash' in prescription;
  const tryStored = hasStoredMemedPdf(prescription) || !knowsDocuments;

  const hasExternal = Boolean(prescription.memed_pdf_url || prescription.memed_digital_link);
  if (!tryStored && hasExternal) {
    openMemedUrl(String(prescription.memed_pdf_url || prescription.memed_digital_link));
    return { opened: true };
  }
  if (!tryStored && !refreshIfMissing) {
    return {
      opened: false,
      message:
        'Esta receita Memed ainda não tem PDF nem link digital salvos. Clique em recuperar ou aguarde alguns segundos.',
    };
  }

  // A aba precisa ser aberta dentro do gesto do usuário: depois do `await` os
  // navegadores tratam `window.open` como pop-up e bloqueiam.
  const pendingTab = window.open('', '_blank');
  if (pendingTab) {
    pendingTab.document.write(
      '<title>Receita Memed</title><p style="font:14px system-ui;padding:24px">Abrindo a receita…</p>',
    );
  }

  if (tryStored && (await openStoredMemedPdf(prescription, pendingTab))) return { opened: true };
  if (hasExternal) {
    const url = String(prescription.memed_pdf_url || prescription.memed_digital_link);
    if (pendingTab && !pendingTab.closed) pendingTab.location.replace(url);
    else openMemedUrl(url);
    return { opened: true };
  }
  if (!refreshIfMissing) {
    pendingTab?.close();
    return {
      opened: false,
      message:
        'Esta receita Memed ainda não tem PDF nem link digital salvos. Clique em recuperar ou aguarde alguns segundos.',
    };
  }

  try {
    const documents = await refreshMemedDocuments(prescription.id);
    if (hasStoredMemedPdf(documents)) {
      const opened = await openStoredMemedPdf({ ...documents, id: prescription.id }, pendingTab);
      if (opened) return { opened: true, documents };
    }
    const url = documents.memed_pdf_url || documents.memed_digital_link;
    if (url) {
      if (pendingTab && !pendingTab.closed) pendingTab.location.replace(url);
      else openMemedUrl(url);
      return { opened: true, documents };
    }
    pendingTab?.close();
    return {
      opened: false,
      documents,
      message:
        'A Memed ainda não disponibilizou os documentos desta receita. Aguarde alguns segundos e tente novamente.',
    };
  } catch (error: any) {
    pendingTab?.close();
    return {
      opened: false,
      message: memedErrorMessage(error, 'Não foi possível abrir a receita na Memed.'),
    };
  }
}

export async function fetchMemedProfessionalStatus(
  professionalId: string,
): Promise<MemedProfessionalStatus> {
  const { data } = await axios.get(getApiUrl(`/clinic/memed/professionals/${professionalId}/status`));
  return data.data;
}

export async function syncMemedProfessional(professionalId: string) {
  const { data } = await axios.post(getApiUrl(`/clinic/memed/professionals/${professionalId}/sync`));
  return data.data;
}

export interface MemedIngredient {
  id: number;
  nome: string;
  nomeEn: string | null;
  slug: string | null;
  relacionados: string | null;
}

/** Princípios ativos da base Memed (60k+ itens, atualizada diariamente). */
export async function searchMemedMedications(terms: string, limit = 20): Promise<MemedIngredient[]> {
  const { data } = await axios.get(getApiUrl('/clinic/memed/medications'), {
    params: { terms, limit },
  });
  return Array.isArray(data?.data) ? data.data : [];
}

/**
 * Resumo textual da receita Memed para os impressos da evolução — o documento
 * oficial continua sendo o PDF assinado, aqui só descrevemos o que foi prescrito.
 */
export function buildMemedPrescriptionSummary(prescription: any): string {
  const items: any[] = Array.isArray(prescription?.items) ? prescription.items : [];
  const lines = items.map((item, index) => {
    const name = [item.medication_name, item.concentration].filter(Boolean).join(' ');
    const details = [item.quantity, item.dosage].filter(Boolean).join(' — ');
    return `${index + 1}. ${name}${details ? `\n   ${details}` : ''}`;
  });

  if (prescription?.memed_digital_link) {
    lines.push('');
    lines.push(
      `Receita digital: ${prescription.memed_digital_link}${
        prescription.memed_unlock_code ? ` (código ${prescription.memed_unlock_code})` : ''
      }`,
    );
  }

  return lines.join('\n').trim() || 'Receita digital emitida na Memed.';
}

/** Mensagem de erro do backend Memed, já legível para o usuário final. */
export function memedErrorMessage(error: any, fallback = 'Não foi possível abrir a Memed.'): string {
  const data = error?.response?.data;
  const missing: string[] = Array.isArray(data?.missing_fields) ? data.missing_fields : [];
  const base = data?.error || error?.message || fallback;
  if (missing.length > 0 && !String(base).includes(missing[0])) {
    return `${base} (${missing.join(', ')})`;
  }
  return base;
}

// ---------------------------------------------------------------------------
// Script singleton
// ---------------------------------------------------------------------------

type MemedRuntime = {
  MdSinapsePrescricao: NonNullable<Window['MdSinapsePrescricao']>;
  MdHub: NonNullable<Window['MdHub']>;
};

let runtimePromise: Promise<MemedRuntime> | null = null;
/** Token atualmente aplicado ao singleton do SDK. */
let loadedToken: string | null = null;

function waitForRuntime(timeoutMs = 30000): Promise<MemedRuntime> {
  return new Promise((resolve, reject) => {
    const startedAt = Date.now();
    const tick = () => {
      if (window.MdSinapsePrescricao && window.MdHub) {
        resolve({ MdSinapsePrescricao: window.MdSinapsePrescricao, MdHub: window.MdHub });
        return;
      }
      if (Date.now() - startedAt > timeoutMs) {
        reject(new Error('A Memed não respondeu no tempo esperado. Verifique sua conexão.'));
        return;
      }
      window.setTimeout(tick, 120);
    };
    tick();
  });
}

/**
 * Carrega o script da Memed no `<head>`, uma vez por sessão. Chamadas
 * concorrentes compartilham a mesma promise; chamadas posteriores com o mesmo
 * token são no-op.
 */
export function loadMemedScript(session: MemedSession): Promise<MemedRuntime> {
  if (runtimePromise) {
    if (loadedToken === session.token) return runtimePromise;

    // A Memed exige script único em SPA. O próprio SDK expõe setToken para
    // renovar o prescritor sem remover o script/iframes nem duplicar listeners.
    runtimePromise = runtimePromise.then((runtime) => {
      if (loadedToken !== session.token) {
        if (typeof runtime.MdSinapsePrescricao.setToken !== 'function') {
          throw new Error(
            'O SDK da Memed não permite trocar o prescritor nesta sessão. Recarregue a página.',
          );
        }
        runtime.MdSinapsePrescricao.setToken(session.token);
        loadedToken = session.token;
      }
      return runtime;
    });
    return runtimePromise;
  }

  const existing = document.getElementById(MEMED_SCRIPT_ID);
  if (existing) {
    runtimePromise = waitForRuntime().then((runtime) => {
      if (loadedToken !== session.token) {
        if (typeof runtime.MdSinapsePrescricao.setToken !== 'function') {
          throw new Error(
            'O SDK da Memed já está carregado com outro prescritor. Recarregue a página.',
          );
        }
        runtime.MdSinapsePrescricao.setToken(session.token);
        loadedToken = session.token;
      }
      return runtime;
    });
    return runtimePromise;
  }

  loadedToken = session.token;
  runtimePromise = new Promise<MemedRuntime>((resolve, reject) => {
    const script = document.createElement('script');
    script.id = MEMED_SCRIPT_ID;
    script.type = 'text/javascript';
    script.src = session.script_url;
    script.setAttribute('data-token', session.token);
    // Cor institucional do Klinvia no módulo da Memed.
    script.setAttribute('data-color', '#0f766e');
    script.onerror = () => {
      script.remove();
      runtimePromise = null;
      loadedToken = null;
      reject(new Error('Falha ao carregar o script da Memed.'));
    };
    script.onload = () => {
      waitForRuntime().then(resolve).catch(reject);
    };
    document.head.appendChild(script);
  });

  return runtimePromise;
}

/** Estado do script — usado para decidir entre carregar e apenas reabrir. */
export function isMemedScriptLoaded(token?: string): boolean {
  if (!document.getElementById(MEMED_SCRIPT_ID)) return false;
  return token ? loadedToken === token : true;
}
